/* global process */
// @ts-check

import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import HttpClient from '../../services/HttpClient'
import AppContext from '../../contexts/AppContext'

import SectionTabs from './components/SectionTabs'
import CharactersToolbar from './components/CharactersToolbar'
import CharacterGrid from './components/CharacterGrid'
import MainCharacterHero from './components/MainCharacterHero'
import './components/Characters.styles.css'

/**
 * @typedef {Object} Character
 * @property {string|number} characterId
 * @property {string} slug
 * @property {string} name
 * @property {string=} clan
 * @property {string=} rank
 * @property {number=} level
 * @property {boolean=} main
 */

export default function CharactersPage({ funcNav = () => {}, funcCharNavbar = () => {} }) {
  const navigate = useNavigate()
  const { user, refreshActiveCharacter } = /** @type {any} */ (useContext(AppContext))

  const [activeTab, setActiveTab] = useState('military')
  const [characters, setCharacters] = useState(/** @type {Character[]} */ ([]))
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const [search, setSearch] = useState('')
  const [sort, setSort] = useState('recent')

  const maxSlotsMilitary = Number(user?.slotsShinobi || 0)
  const maxSlotsCivilian = Number(user?.slotsCivilian || 0)

  // Lista sin Navbar global (escape vía masthead). CharNavbar solo en ficha.
  useEffect(() => {
    if (typeof funcNav === 'function') funcNav(false)
    if (typeof funcCharNavbar === 'function') funcCharNavbar(false)
  }, [funcNav, funcCharNavbar])

  useEffect(() => {
    loadCharacters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadCharacters = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const { data } = await HttpClient().get('/api/v1/characters/mine')
      setCharacters(Array.isArray(data) ? data : [])
    } catch (err) {
      setError(err?.message || 'Error al cargar personajes')
    } finally {
      setIsLoading(false)
    }
  }

  const filteredCharacters = useMemo(() => {
    let list = [...characters]
    const q = search.trim().toLowerCase()
    if (q) {
      list = list.filter((c) => {
        const name = `${c?.name || ''}`.toLowerCase()
        const clan = `${c?.clan || ''}`.toLowerCase()
        const rank = `${c?.rank || ''}`.toLowerCase()
        return name.includes(q) || clan.includes(q) || rank.includes(q)
      })
    }

    if (sort === 'alpha') {
      list.sort((a, b) => {
        const an = `${a?.clan || ''} ${a?.name || ''}`.trim()
        const bn = `${b?.clan || ''} ${b?.name || ''}`.trim()
        return an.localeCompare(bn)
      })
    } else if (sort === 'favorite') {
      list.sort((a, b) => {
        const am = a?.main ? 0 : 1
        const bm = b?.main ? 0 : 1
        if (am !== bm) return am - bm
        return String(a?.name || '').localeCompare(String(b?.name || ''))
      })
    }

    return list
  }, [characters, search, sort])

  const gridItems = useMemo(() => {
    return filteredCharacters.map((c) => {
      const imageUrl = `/assets/images/${c.slug}.png`
      return {
        id: c.characterId,
        slug: c.slug,
        name: c.name,
        clan: c.clan,
        rank: c.rank,
        level: c.level,
        main: !!c.main,
        imageUrl,
      }
    })
  }, [filteredCharacters])

  const mainCharacter = useMemo(() => {
    const row = characters.find((c) => !!c.main)
    if (!row) return null
    return {
      ...row,
      imageUrl: `/assets/images/${row.slug}.png`,
    }
  }, [characters])

  const militaryCounts = useMemo(
    () => ({ used: characters.length, total: maxSlotsMilitary }),
    [characters.length, maxSlotsMilitary]
  )
  const civilianCounts = useMemo(
    () => ({ used: 0, total: maxSlotsCivilian }),
    [maxSlotsCivilian]
  )

  const emptySlotsCount = useMemo(() => {
    if (activeTab === 'civilian') return 0
    return Math.max(0, maxSlotsMilitary - characters.length)
  }, [activeTab, maxSlotsMilitary, characters.length])

  const handleCreateClick = () => navigate('/personajes/crear')
  const handleCardClick = (slug) => navigate(`/personajes/${slug}`)

  const handleToggleMain = async (charId, nextMain) => {
    try {
      await HttpClient().put('/api/v1/characters/changeMain', {
        charId,
        main: nextMain,
      })
      setCharacters((prev) =>
        prev.map((c) => {
          if (c.characterId === charId) return { ...c, main: nextMain }
          if (nextMain) return { ...c, main: false }
          return c
        })
      )
      // Keep AppContext actor in sync (adventure/misiones/presence)
      if (typeof refreshActiveCharacter === 'function') {
        await refreshActiveCharacter()
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('No se pudo actualizar principal:', err)
    }
  }

  const renderLoading = () => (
    <div className="charactersGrid" aria-label="Cargando personajes">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="skeletonCard" />
      ))}
    </div>
  )

  const renderError = () => (
    <div className="charactersAlert" role="alert">
      <p>{error}</p>
      <button type="button" className="mainHero__cta" onClick={loadCharacters}>
        Reintentar
      </button>
    </div>
  )

  return (
    <div className="page charactersPageShell">
      <div className="charactersPage">
        <header className="charactersMasthead">
          <div className="charactersMasthead__brand">
            <span className="charactersMasthead__seal" aria-hidden="true">
              忍
            </span>
            <div>
              <p className="charactersMasthead__eyebrow">Escuadra · 組</p>
              <h1 className="charactersMasthead__title">Mis shinobis</h1>
            </div>
          </div>

          <div className="charactersMasthead__slots" title="Slots militares">
            <span className="charactersMasthead__slots-label">Slots</span>
            <span className="charactersMasthead__slots-value">
              {militaryCounts.used} / {militaryCounts.total || '—'}
            </span>
          </div>

          <nav className="charactersMasthead__links" aria-label="Salir de la escuadra">
            <Link to="/">Inicio</Link>
            <Link to="/personajes">Directorio</Link>
            <Link to="/mapa">Mapa</Link>
            <Link to="/aventuras">Aventuras</Link>
          </nav>
        </header>

        <MainCharacterHero
          character={mainCharacter}
          onEnter={handleCardClick}
          onCreate={handleCreateClick}
        />

        <div className="charactersHeader">
          <SectionTabs
            activeTab={activeTab}
            onChangeTab={setActiveTab}
            militaryCounts={militaryCounts}
            civilianCounts={civilianCounts}
          />
        </div>

        {activeTab === 'military' && (
          <CharactersToolbar search={search} onSearchChange={setSearch} sort={sort} onSortChange={setSort} />
        )}

        {isLoading && renderLoading()}
        {!isLoading && error && renderError()}
        {!isLoading && !error && activeTab === 'military' && (
          <CharacterGrid
            items={gridItems}
            emptySlotsCount={emptySlotsCount}
            onCreateClick={handleCreateClick}
            onCardClick={handleCardClick}
            onToggleMain={handleToggleMain}
          />
        )}
        {!isLoading && !error && activeTab === 'civilian' && (
          <div className="civilianSoon" role="status">
            <span className="civilianSoon__seal" aria-hidden="true">
              民
            </span>
            <h2 className="civilianSoon__title">Civiles — próximamente</h2>
            <p className="civilianSoon__text">
              Los slots civiles ({civilianCounts.total || 0}) estarán disponibles en una entrega
              posterior. Por ahora solo se gestionan shinobis militares.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

CharactersPage.propTypes = {
  funcNav: PropTypes.func,
  funcCharNavbar: PropTypes.func,
}
